import safeStringify from 'fast-safe-stringify'
import { useCallback, useEffect, useState } from 'react'

function poll(execution: () => void | Promise<void>, interval: number) {
  let isExecuting = false
  const execute = async () => {
    if (!isExecuting) {
      isExecuting = true
      try {
        await execution()
      } catch (err) {
        console.error(err)
      }
      // eslint-disable-next-line require-atomic-updates
      isExecuting = false
    }
  }

  const timeoutId = setTimeout(execute, 0)
  const intervalId = setInterval(execute, interval)

  return function stopBackgroundWorker() {
    clearTimeout(timeoutId)
    clearInterval(intervalId)
  }
}

function isObjectChanged<T>(a: T, b: T): boolean {
  const options = { depthLimit: 5, edgesLimit: undefined }
  return (
    safeStringify(a, undefined, undefined, options) !==
    safeStringify(b, undefined, undefined, options)
  )
}

export function usePoll<T>(
  execute: () => Promise<T>,
  deps: ReadonlyArray<any>,
  interval = 5000,
): [T | undefined, () => Promise<void>] {
  const [data, setData] = useState<T | undefined>()

  const pollData = useCallback(async () => {
    try {
      const data = await execute()
      if (!data) {
        return setData(data)
      }
      setData((prev) => {
        const changed = !prev || isObjectChanged<T>(prev, data)
        return changed ? data : prev
      })
    } catch (err) {
      console.error(
        `Error during polling data: ${(err as { message: string }).message}`,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => poll(pollData, interval), [pollData, interval])

  return [data, pollData]
}
